import { Button, Col, Form, Row } from "react-bootstrap";
import { Typography } from "../../components/utilities/Typhograpy";
import { FormProvider, useForm } from "react-hook-form";
import { getUserInfo, updatePaidLeave } from "../../api/UsersApi";
import { useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import GlobalContext from "../../context/GlobalContext";
import BoxSkeleton from "../../components/utilities/BoxSkeleton";
import BackButton from "../../components/button/BackButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faUpload } from "@fortawesome/free-solid-svg-icons";
import swal from "../../services/swalConfig";
import InputWorkingDaysTime from "../../components/InputWorkingDaysTime";
import { handleUpdateSchedule } from "../../api/WorkspaceApi";
import TimeZoneSelect from "../../components/TimeZoneSelect";
import ModalImportSchedule from "../../components/modal/ModalImportSchedule";

import { tr } from "../../i18n";
const UsersEditSchedule = () => {
  const isMounted = useRef(true);
  const location = useLocation();
  const methods = useForm();
  const personnelId = location.state?.personnel_id || null;
  const [personelData, setPersonelData] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [schedules, setSchedules] = useState(null);
  const { user, setLoading } = useContext(GlobalContext);
  const {
    handleSubmit,
    formState: { errors },
    setError,
  } = methods;
  const [timeZone, setTimeZone] = useState(null);

  const loadPersonnelInformation = async () => {
    setPersonelData(null);

    try {
      const response = await getUserInfo(personnelId);

      if (response.status == 200) {
        const defaultTimeZone =
          getTimeZone(response.data.schedules) ||
          response.data.internal_time_zone ||
          null;
        if (defaultTimeZone) {
          setTimeZone({ text: defaultTimeZone, id: defaultTimeZone });
        } else {
          setTimeZone(null);
        }

        setPersonelData(response.data);
        setSchedules({ schedules: response.data.schedules });
      } else if (response.status == 404) {
        setPersonelData([]);
      }
    } catch (error) {
      setPersonelData(null);
    }
  };

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }

    loadPersonnelInformation();
  }, [user]);

  const getTimeZone = (data) => {
    if (!Array.isArray(data) || data.length === 0) {
      return null;
    }

    const frequencyMap = data.reduce((acc, item) => {
      if (item.time_zone) {
        acc[item.time_zone] = (acc[item.time_zone] || 0) + 1;
      }
      return acc;
    }, {});

    // Jika tidak ada time_zone yang valid, kembalikan null
    if (Object.keys(frequencyMap).length === 0) {
      return null;
    }

    return Object.keys(frequencyMap).reduce((a, b) =>
      frequencyMap[a] > frequencyMap[b] ? a : b,
    );
  };

  const handleImport = (selectedSchedule) => {
    setSchedules({ schedules: selectedSchedule });
    setOpenModal(false);
  };

  return (
    <Row>
      <Col lg={12}>
        <div className="box-shadow">
          {personelData == null ? (
            <BoxSkeleton count="6" size="12" height="20px" />
          ) : (
            <FormProvider {...methods}>
              <Form
                onSubmit={handleSubmit(async (data) => {
                  try {
                    setLoading(true);

                    const newData = {
                      company_id: user.workspace.company_id,
                      personnel_id: personnelId,
                      schedule: data.schedule,
                      timeZone: data.timeZone,
                    };

                    const response = await handleUpdateSchedule(newData);

                    if (response.status === 200) {
                      swal.fire({
                        title: tr("users:congratulations"),
                        text: response.message,
                        icon: "success",
                        showCancelButton: false,
                      });

                      // Refresh data setelah sukses update
                      loadPersonnelInformation();
                    } else if (response.status === 400) {
                      Object.entries(response.errors).forEach(
                        ([key, message]) =>
                          setError(key, { type: "manual", message }),
                      );
                    } else {
                      swal.fire({
                        title: tr("common:errors.oops"),
                        text: response.message,
                        icon: "error",
                        showCancelButton: false,
                      });
                    }
                  } catch (error) {
                    swal.fire({
                      title: tr("common:errors.oops"),
                      text: tr("users:something_went_wrong_try_logging_in"),
                      icon: "error",
                      showCancelButton: false,
                    });
                  } finally {
                    setLoading(false);
                  }
                })}
              >
                <Form.Group as={Row} className="mb-2">
                  <Form.Label column sm="3">
                    <Typography variant="form">{tr("users:id")}</Typography>
                  </Form.Label>
                  <Col sm="9">
                    <Form.Control
                      plaintext
                      readOnly
                      defaultValue={personelData.internal_id || "-"}
                    />
                  </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-2">
                  <Form.Label column sm="3">
                    <Typography variant="form">{tr("shared:name")}</Typography>
                  </Form.Label>
                  <Col sm="9">
                    <Form.Control
                      plaintext
                      readOnly
                      defaultValue={personelData.personal_name}
                    />
                  </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-2">
                  <Form.Label column sm="3">
                    <Typography variant="form">
                      {tr("shared:time_zone")}{" "} <span class="text-danger">*</span>
                    </Typography>
                  </Form.Label>
                  <Col sm="9">
                    <TimeZoneSelect
                      name="timeZone"
                      rules={{ required: tr("shared:time_zone_is_required") }}
                      defaultValue={timeZone}
                    />
                    {errors.timeZone ? (
                      <small className="text-danger">
                        {errors.timeZone.message}
                      </small>
                    ) : (
                      ""
                    )}
                  </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3">
                  <Col sm={3}>
                    <Form.Label>
                      <Typography variant="form">
                        {tr("common:breadcrumb.schedule")}{" "} <span className="text-danger">*</span>
                      </Typography>
                    </Form.Label>

                    <Button
                      variant="outline-primary"
                      className="d-flex align-items-center gap-2 px-4 py-2 rounded-pill shadow-sm mt-2"
                      onClick={() => setOpenModal(true)}
                    >
                      <FontAwesomeIcon icon={faUpload} />
                      {tr("common:breadcrumb.import")}
                    </Button>

                    {errors.schedule && (
                      <small className="text-danger">
                        {errors.schedule.message}
                      </small>
                    )}
                  </Col>

                  <Col sm="9">
                    <InputWorkingDaysTime defaultValue={schedules} />
                    {errors.days ? (
                      Object.keys(errors.days).length > 0 ? (
                        <small className="text-danger">
                          {errors.days.message}
                        </small>
                      ) : (
                        ""
                      )
                    ) : (
                      ""
                    )}
                  </Col>
                </Form.Group>
                <Row>
                  <Col
                    lg={12}
                    md={12}
                    sm={12}
                    className="mt-3 d-flex justify-content-between mt-5"
                  >
                    <BackButton params={{ personnel_id: personnelId }} />
                    <Button type="submit" className="btn btn-primary">
                      <FontAwesomeIcon icon={faCheckCircle} /> {" "}{tr("users:update_schedule")}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </FormProvider>
          )}
          <ModalImportSchedule
            show={openModal}
            onClose={() => setOpenModal(false)}
            onSelect={handleImport}
          />
        </div>
      </Col>
    </Row>
  );
};

export default UsersEditSchedule;
